import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Plus, Users, Trash2, Copy, Check, Clock, Shield, ShieldCheck, BarChart3, ListChecks, Bell } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminListStaff, adminInviteStaff, adminUpdateStaff, adminDeleteStaff,
  adminRevokeInvite, adminTaskNotifications, adminMarkTaskRead, fmtErr,
} from '@/lib/staffApi';
import { getFurnitureProjects } from '@/lib/api';
import { TaskNotificationBell } from '@/components/staff/TaskNotificationBell';

const ROLES = [
  { value: 'designer',  label: 'Designer' },
  { value: 'assembly',  label: 'Assembly' },
  { value: 'housekeeper', label: 'Housekeeper' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other',     label: 'Other' },
];

const PERM_LEVELS = [
  { value: 'full',       label: 'Full project — edit + share' },
  { value: 'commenter',  label: 'View & comment — no edits' },
  { value: 'limited',    label: 'Pictures + rooms — no financials' },
];

const ROLE_DEFAULT_PERM = {
  designer: 'full',
  assembly: 'limited',
  housekeeper: 'limited',
  maintenance: 'limited',
  other: 'limited',
};

const PERM_BADGE = {
  full:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  commenter: 'bg-blue-50 text-blue-700 border-blue-200',
  limited:   'bg-muted text-muted-foreground border-border',
};

export function FurnitureStaffingPage() {
  const navigate = useNavigate();
  const adminPass = typeof window !== 'undefined' ? sessionStorage.getItem('app_password') || '' : '';
  const [data, setData] = useState({ users: [], pending_invites: [] });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const refresh = useCallback(async () => {
    if (!adminPass) return;
    setLoading(true);
    try {
      const [s, p] = await Promise.all([adminListStaff(adminPass), getFurnitureProjects()]);
      setData(s); setProjects(p);
    } catch (e) {
      toast.error(fmtErr(e, 'Failed to load staff'));
    } finally { setLoading(false); }
  }, [adminPass]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="furniture-staffing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Furniture Project Staffing</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage designers, assembly, housekeeping &amp; maintenance accounts.</p>
        </div>
        <div className="flex items-center gap-2">
          <TaskNotificationBell
            fetcher={() => adminTaskNotifications(adminPass)}
            markRead={(id) => adminMarkTaskRead(adminPass, id)}
            onOpenTask={(t) => navigate(`/furniture-projects/staffing/tasks?task=${t.id}`)}
            testId="staffing-task-bell"
          />
          <Button variant="outline" onClick={() => navigate('/furniture-projects/staffing/tasks')} data-testid="staff-tasks-btn"><ListChecks className="h-4 w-4 mr-1.5" />Tasks</Button>
          <Button variant="outline" onClick={() => navigate('/furniture-projects/staffing/hours')} data-testid="staff-chart-btn"><BarChart3 className="h-4 w-4 mr-1.5" />Hours Log</Button>
          <Button onClick={() => setShowInvite(true)} data-testid="staff-invite-btn"><Plus className="h-4 w-4 mr-1.5" />Invite Staff</Button>
        </div>
      </div>

      {/* Pending invites */}
      {data.pending_invites.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" />Pending sign-up codes ({data.pending_invites.length})</CardTitle></CardHeader>
          <CardContent className="divide-y">
            {data.pending_invites.map(inv => (
              <div key={inv.id} className="py-2 flex items-center gap-3" data-testid="pending-invite-row">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.name}{inv.email && <span className="text-xs text-muted-foreground"> ({inv.email})</span>}</p>
                  <p className="text-xs text-muted-foreground capitalize">{inv.role}</p>
                </div>
                <PinDisplay pin={inv.pin} />
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={async () => {
                  if (!window.confirm('Revoke this invite?')) return;
                  await adminRevokeInvite(adminPass, inv.id); refresh();
                }} data-testid="revoke-invite-btn"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active staff */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Staff members ({data.users.length})</CardTitle></CardHeader>
        <CardContent>
          {data.users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No staff yet — click "Invite Staff" to get started.</p>
          ) : (
            <div className="divide-y">
              {data.users.map(u => (
                <div key={u.id} className="py-3 flex items-center gap-3" data-testid="staff-row">
                  <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {(u.name || u.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                      {u.name || '—'}
                      {u.is_approver && <ShieldCheck className="h-3 w-3 text-emerald-600" title="Approver" />}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{u.email} · <span className="capitalize">{u.role}</span></p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] border ${PERM_BADGE[u.permission_level] || PERM_BADGE.limited}`}>{(PERM_LEVELS.find(p => p.value === u.permission_level)?.label || u.permission_level).split(' — ')[0]}</Badge>
                  <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right">{u.project_ids?.length || 0} project{(u.project_ids?.length || 0) === 1 ? '' : 's'}</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingUser(u)} data-testid="staff-edit-btn">Edit</Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={async () => {
                    if (!window.confirm(`Delete ${u.name}? This is permanent.`)) return;
                    await adminDeleteStaff(adminPass, u.id); refresh();
                  }} data-testid="staff-delete-btn"><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <InviteDialog open={showInvite} onOpenChange={setShowInvite} adminPass={adminPass} projects={projects} onCreated={refresh} />
      <EditStaffDialog user={editingUser} onOpenChange={() => setEditingUser(null)} adminPass={adminPass} projects={projects} onSaved={refresh} />
    </div>
  );
}

/* ── PIN display with copy ───────────────────────────────────────── */
function PinDisplay({ pin }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(pin); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success('PIN copied'); }}
      className="font-mono text-xs px-2 py-1 rounded bg-amber-50 text-amber-900 border border-amber-200 inline-flex items-center gap-1 hover:bg-amber-100"
      data-testid="invite-pin"
      title="Click to copy"
    >
      {pin}{copied ? <Check className="h-3 w-3 ml-0.5" /> : <Copy className="h-3 w-3 ml-0.5 opacity-60" />}
    </button>
  );
}

/* ── Invite dialog ───────────────────────────────────────────────── */
function InviteDialog({ open, onOpenChange, adminPass, projects, onCreated }) {
  const [form, setForm] = useState({ name: '', role: 'designer', permission_level: 'full', is_approver: false, project_ids: [], master_dynamic_ids: [] });
  const [busy, setBusy] = useState(false);
  const [createdPin, setCreatedPin] = useState(null);

  useEffect(() => {
    if (open) { setForm({ name: '', role: 'designer', permission_level: 'full', is_approver: false, project_ids: [], master_dynamic_ids: [] }); setCreatedPin(null); }
  }, [open]);

  const onRoleChange = (role) => setForm(f => ({ ...f, role, permission_level: ROLE_DEFAULT_PERM[role] || 'limited' }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const inv = await adminInviteStaff(adminPass, form);
      setCreatedPin(inv.pin);
      onCreated();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  const toggleProject = (id) => setForm(f => ({ ...f, project_ids: f.project_ids.includes(id) ? f.project_ids.filter(x => x !== id) : [...f.project_ids, id] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Invite a staff member</DialogTitle>
          <DialogDescription>Generate a one-time sign-up code. The staff member fills in their own email &amp; password when they redeem it.</DialogDescription>
        </DialogHeader>
        {createdPin ? (
          <div className="text-center py-4 space-y-3" data-testid="invite-success">
            <p className="text-sm">Share this code with <span className="font-medium">{form.name}</span>:</p>
            <div className="text-3xl font-mono font-bold text-amber-900 bg-amber-50 border border-amber-200 rounded-xl py-4">{createdPin}</div>
            <p className="text-xs text-muted-foreground">They visit <span className="font-mono">/staff/register</span>, enter this code, and finish setting up their account. The code stops working as soon as they sign up.</p>
            <Button className="w-full" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Staff member's full name" data-testid="invite-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Role</Label>
              <Select value={form.role} onValueChange={onRoleChange}>
                <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Permission level</Label>
              <Select value={form.permission_level} onValueChange={v => setForm(f => ({ ...f, permission_level: v }))}>
                <SelectTrigger data-testid="invite-permission"><SelectValue /></SelectTrigger>
                <SelectContent>{PERM_LEVELS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ProjectAccessSelector
              projects={projects}
              selected={form.project_ids}
              onToggle={toggleProject}
              dynamicMasters={form.master_dynamic_ids}
              onDynamicChange={(ids) => setForm(f => ({ ...f, master_dynamic_ids: ids }))}
            />
            <label className="flex items-center gap-2 text-sm pt-1">
              <input type="checkbox" checked={form.is_approver} onChange={e => setForm(f => ({ ...f, is_approver: e.target.checked }))} className="rounded" data-testid="invite-approver" />
              <Shield className="h-3.5 w-3.5 text-emerald-600" /> Can approve other staff's edit suggestions
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="invite-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}Generate code</Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Edit-staff dialog ───────────────────────────────────────────── */
function EditStaffDialog({ user, onOpenChange, adminPass, projects, onSaved }) {
  const [form, setForm] = useState({ name: '', role: 'designer', permission_level: 'full', is_approver: false, project_ids: [], master_dynamic_ids: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (user) setForm({ name: user.name || '', role: user.role, permission_level: user.permission_level || 'limited', is_approver: !!user.is_approver, project_ids: user.project_ids || [], master_dynamic_ids: user.master_dynamic_ids || [] });
  }, [user]);
  if (!user) return null;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await adminUpdateStaff(adminPass, user.id, form); toast.success('Staff updated'); onSaved(); onOpenChange(); }
    catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Edit {user.name}</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs text-muted-foreground mb-1 block">Name</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Role</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">Permission level</Label>
            <Select value={form.permission_level} onValueChange={v => setForm(f => ({ ...f, permission_level: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PERM_LEVELS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <ProjectAccessSelector
            projects={projects}
            selected={form.project_ids}
            onToggle={(id) => setForm(f => ({ ...f, project_ids: f.project_ids.includes(id) ? f.project_ids.filter(x => x !== id) : [...f.project_ids, id] }))}
            dynamicMasters={form.master_dynamic_ids || []}
            onDynamicChange={(ids) => setForm(f => ({ ...f, master_dynamic_ids: ids }))}
          />
          <label className="flex items-center gap-2 text-sm pt-1">
            <input type="checkbox" checked={form.is_approver} onChange={e => setForm(f => ({ ...f, is_approver: e.target.checked }))} className="rounded" />
            <Shield className="h-3.5 w-3.5 text-emerald-600" /> Approver
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onOpenChange}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-staff-save">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProjectAccessSelector({ projects, selected, onToggle, dynamicMasters = [], onDynamicChange }) {
  // Group projects by master / sub / standalone
  const { masters, standalone } = (() => {
    const m = []; const s = []; const byParent = new Map();
    for (const p of projects) {
      if (p.is_master) { m.push(p); continue; }
      if (p.parent_id) {
        const arr = byParent.get(p.parent_id) || [];
        arr.push(p); byParent.set(p.parent_id, arr);
      } else {
        s.push(p);
      }
    }
    const withSubs = m.map(master => ({ ...master, _subs: byParent.get(master.id) || [] }));
    // Orphans → standalone
    for (const [pid, subs] of byParent) {
      if (!m.find(x => x.id === pid)) s.push(...subs);
    }
    return { masters: withSubs, standalone: s };
  })();

  const totalSelected = selected.length + dynamicMasters.length;
  const totalProjects = projects.length;

  const setMasterMode = (master, mode) => {
    const subIds = (master._subs || []).map(s => s.id);
    if (mode === 'none') {
      onDynamicChange?.(dynamicMasters.filter(x => x !== master.id));
      // Also drop any sub-units & the master itself from explicit project_ids
      const toDrop = new Set([master.id, ...subIds]);
      for (const id of toDrop) {
        if (selected.includes(id)) onToggle(id);
      }
    } else if (mode === 'all') {
      // Add master to dynamic, drop any explicit ids for this group
      if (!dynamicMasters.includes(master.id)) {
        onDynamicChange?.([...dynamicMasters, master.id]);
      }
      const toDrop = new Set([master.id, ...subIds]);
      for (const id of toDrop) {
        if (selected.includes(id)) onToggle(id);
      }
    } else if (mode === 'specific') {
      // Drop master from dynamic. Selection of sub-units is left to the user.
      onDynamicChange?.(dynamicMasters.filter(x => x !== master.id));
    }
  };

  const masterMode = (master) => {
    if (dynamicMasters.includes(master.id)) return 'all';
    const subIds = (master._subs || []).map(s => s.id);
    if (subIds.some(id => selected.includes(id)) || selected.includes(master.id)) return 'specific';
    return 'none';
  };

  return (
    <div>
      <Label className="text-xs text-muted-foreground mb-1 block">
        Project access ({totalSelected} of {totalProjects} + {dynamicMasters.length} dynamic master{dynamicMasters.length === 1 ? '' : 's'})
      </Label>
      <div className="border rounded-lg max-h-60 overflow-y-auto">
        {projects.length === 0 ? (
          <p className="text-xs text-muted-foreground p-3">No furniture projects yet.</p>
        ) : (
          <div className="divide-y">
            {/* Master groups */}
            {masters.map(master => {
              const mode = masterMode(master);
              return (
                <div key={master.id} className="px-2.5 py-2 bg-emerald-50/30">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        🏢 {master.name}
                        <span className="text-[10px] uppercase tracking-wider ml-1.5 text-emerald-700 font-semibold">Master</span>
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {(master._subs || []).length} sub-unit{(master._subs || []).length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="inline-flex text-[10px] rounded-full border border-emerald-300/60 p-0.5 bg-white flex-shrink-0">
                      <button type="button"
                        className={`px-2 py-0.5 rounded-full transition-colors ${mode === 'none' ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/50'}`}
                        onClick={() => setMasterMode(master, 'none')}
                        data-testid={`pas-mode-none-${master.id}`}
                      >None</button>
                      <button type="button"
                        className={`px-2 py-0.5 rounded-full transition-colors ${mode === 'all' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:bg-emerald-50'}`}
                        onClick={() => setMasterMode(master, 'all')}
                        title="Auto-includes all current and future sub-units"
                        data-testid={`pas-mode-all-${master.id}`}
                      >All (dynamic)</button>
                      <button type="button"
                        className={`px-2 py-0.5 rounded-full transition-colors ${mode === 'specific' ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:bg-emerald-50'}`}
                        onClick={() => setMasterMode(master, 'specific')}
                        data-testid={`pas-mode-specific-${master.id}`}
                      >Specific</button>
                    </div>
                  </div>
                  {mode === 'specific' && (master._subs || []).length > 0 && (
                    <div className="mt-1.5 pl-4 space-y-0.5">
                      {(master._subs || []).map(sub => (
                        <label key={sub.id} className="flex items-center gap-2 px-1.5 py-0.5 text-xs hover:bg-muted/40 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={selected.includes(sub.id)}
                            onChange={() => onToggle(sub.id)}
                            className="rounded"
                            data-testid={`pas-sub-${sub.id}`}
                          />
                          <span className="truncate">{sub.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {mode === 'all' && (
                    <p className="mt-1 pl-1 text-[10px] text-emerald-700">
                      ✓ Auto-includes all current and future sub-units of this master.
                    </p>
                  )}
                </div>
              );
            })}

            {/* Standalone projects */}
            {standalone.length > 0 && masters.length > 0 && (
              <p className="px-2.5 py-1 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                Other projects
              </p>
            )}
            {standalone.map(p => (
              <label key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-muted/40 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(p.id)}
                  onChange={() => onToggle(p.id)}
                  className="rounded"
                />
                <span className="truncate">{p.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
